import { useCallback, useState } from 'react';
import { useFig } from '../../session/FigProvider.js';
import type { Agency } from '../../core/api/types.js';
import { safeAccent, toHex, parseColor, composite, WCAG } from '../../core/a11y/contrast.js';
import { detectTimeZone } from '../../core/auth/session.js';
import { ErrorState, RowSkeleton } from '../../components/States.js';
import { Field } from '../../components/Dialog.js';
import { useResource } from './useResource.js';

/**
 * Agency settings: time zone and branding.
 *
 * The accent picker is finding A1 made visible. An agency's brand colour repaints the Client
 * Portal, and nothing used to check it — so an agency with a pale brand made *their clients'*
 * portal unreadable, on a surface the agency itself never signs in to. Their colour is never
 * rejected; the preview simply tells them the truth at the moment they choose it, and Fig
 * derives a legible variant for anything that has to be read.
 *
 * That is feedback rather than friction (checklist N1's counterweight): it does work on their
 * behalf instead of obstructing them.
 */
export function Settings() {
  const { client } = useFig();
  const load = useCallback(() => client.getAgency(), [client]);
  const { data: agency, error, busy, reload } = useResource<Agency>(load);

  if (error !== null) return <ErrorState error={error} onRetry={() => void reload()} />;
  if (agency === null && busy) return <RowSkeleton count={3} />;
  if (agency === null) return null;

  return (
    <>
      <h1 className="page-title">Settings</h1>
      <TimeZoneSection agency={agency} onSaved={() => void reload()} />
      <BrandingSection agency={agency} onSaved={() => void reload()} />
    </>
  );
}

function TimeZoneSection({ agency, onSaved }: { agency: Agency; onSaved: () => void }) {
  const { client } = useFig();
  const [busy, setBusy] = useState(false);
  const detected = detectTimeZone();
  const mismatched = detected !== undefined && detected !== agency.timeZoneId;

  async function applyDetectedZone() {
    if (!detected) return;
    setBusy(true);
    try { await client.updateAgency({ timeZoneId: detected }); onSaved(); } finally { setBusy(false); }
  }

  return (
    <section className="card">
      <h2 className="state-title">Time zone</h2>
      <p className="state-body">
        Days and “today” are worked out in this zone. It was read from the device when the
        account was created — nobody was asked for it.
      </p>
      <p className="setting-value">{agency.timeZoneId}</p>
      {/* A default is only free when getting it wrong is cheap. Getting this one wrong makes
          every date filter silently wrong by hours, so a mismatch is surfaced rather than
          left for someone to notice as odd counts. */}
      {mismatched && (
        <div className="banner" role="status">
          This device says <strong>{detected}</strong>.{' '}
          <button className="linkbtn" onClick={() => void applyDetectedZone()} disabled={busy}>
            {busy ? 'Saving…' : `Use ${detected} instead`}
          </button>
        </div>
      )}
    </section>
  );
}

function BrandingSection({ agency, onSaved }: { agency: Agency; onSaved: () => void }) {
  const { client } = useFig();
  const [displayName, setDisplayName] = useState(agency.brandingDisplayName ?? '');
  const [accent, setAccent] = useState(agency.brandingAccentColor ?? '#0A84FF');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Measured against the surface the portal actually paints on, not against white.
  const surfaceCss = typeof document !== 'undefined'
    ? getComputedStyle(document.documentElement).getPropertyValue('--surface-1').trim()
    : '#ffffff';
  const parsed = parseColor(surfaceCss) ?? parseColor('#ffffff')!;
  const surface = composite(parsed, [255, 255, 255]);
  const safe = safeAccent(accent, surface, WCAG.nonText);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await client.updateAgency({
        brandingDisplayName: displayName.trim(),
        brandingAccentColor: accent,
      });
      onSaved();
    } catch (err) { setError(err instanceof Error ? err.message : 'That did not save.'); }
    finally { setBusy(false); }
  }

  return (
    <section className="card">
      <h2 className="state-title">What your clients see</h2>
      <form onSubmit={save} noValidate>
        <Field id="brand-name" label="Name shown to your clients" value={displayName}
          autoComplete="organization" onChange={(e) => setDisplayName(e.target.value)}
          hint={`Falls back to “${agency.name}” if you leave this empty.`} />

        <div className="field">
          <label className="field-label" htmlFor="brand-accent">Accent colour</label>
          <div className="accent-row">
            <input id="brand-accent" className="input input-color" type="color" value={accent}
              onChange={(e) => setAccent(e.target.value)}
              aria-describedby="brand-accent-check" />
            <span className="accent-swatch" style={{ background: accent }} aria-hidden="true" />
            <code className="accent-hex">{accent}</code>
          </div>

          {safe && (
            <p id="brand-accent-check" className={safe.adjusted ? 'hint hint-warn' : 'hint'}>
              {safe.adjusted ? (
                <>
                  On your clients’ background this reads at{' '}
                  <strong>{safe.rawRatio.toFixed(1)}:1</strong>, below the{' '}
                  {WCAG.nonText}:1 needed to be legible. Fig will keep{' '}
                  <code>{toHex(safe.identity)}</code> for your logo and marks, and use{' '}
                  <code>{toHex(safe.onSurface)}</code> — the same colour, darkened — for
                  anything they have to read. Nothing is rejected.
                </>
              ) : (
                <>Reads at <strong>{safe.rawRatio.toFixed(1)}:1</strong> on your clients’
                  background. Comfortably legible.</>
              )}
            </p>
          )}
        </div>

        {error && <p className="hint hint-error" role="alert">{error}</p>}
        <button className="btn btn-primary btn-inline" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </form>
    </section>
  );
}
